import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { useT } from '@/lib/i18n';

import { resetPassword } from '../api/auth';

function resetSchema(t: ReturnType<typeof useT>) {
  return z
    .object({
      password: z
        .string()
        .min(10, t('auth.common.min_length', { min: 10 }))
        .refine((value) => !/^\d+$/.test(value), t('auth.reset.only_numbers'))
        .refine((value) => new Set(value).size >= 5, t('auth.common.too_repetitive')),
      confirm: z.string(),
    })
    .refine((values) => values.password === values.confirm, {
      message: t('auth.reset.mismatch'),
      path: ['confirm'],
    });
}

type FormValues = z.infer<ReturnType<typeof resetSchema>>;

/**
 * Also serves invited colleagues: an invitation link carries the same kind of
 * single-use token, and setting a password is what activates the account.
 */
export function ResetPasswordPage({ mode = 'reset' }: { mode?: 'reset' | 'invite' }) {
  const t = useT();
  const schema = useMemo(() => resetSchema(t), [t]);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setFormError(null);
    if (!token) {
      setFormError(t('auth.reset.missing_token'));
      return;
    }
    try {
      await resetPassword(token, values.password);
      setDone(true);
    } catch (error) {
      setFormError(
        error instanceof ApiError ? error.message : t('auth.common.generic_error'),
      );
    }
  };

  if (done) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-ink">{t('auth.reset.done_title')}</h1>
        <p className="mt-2 text-sm text-muted">
          {t('auth.reset.done_body')}
        </p>
        <Link
          to="/login"
          className="mt-6 inline-flex h-11 items-center rounded-xl bg-brand-500 px-6 text-sm font-semibold text-white hover:bg-brand-600"
        >
          {t('auth.common.sign_in')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-ink">
        {mode === 'invite' ? t('auth.reset.title_invite') : t('auth.reset.title_reset')}
      </h1>
      <p className="mt-1.5 text-sm text-muted">
        {mode === 'invite' ? t('auth.reset.intro_invite') : t('auth.reset.intro_reset')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-6 space-y-4">
        {formError ? <Alert tone="error">{formError}</Alert> : null}
        {!token ? <Alert tone="error">{t('auth.reset.invalid_link')}</Alert> : null}

        <TextField
          label={t('auth.reset.new_password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.common.min_length_hint', { min: 10 })}
          error={errors.password?.message}
          {...register('password')}
        />
        <TextField
          label={t('auth.reset.confirm_password')}
          type="password"
          autoComplete="new-password"
          error={errors.confirm?.message}
          {...register('confirm')}
        />

        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || !token}>
          {isSubmitting ? t('auth.common.saving') : t('auth.reset.submit')}
        </Button>
      </form>
    </div>
  );
}
